import {EnvTypes, TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {a, functionOf, TGeneric} from '@packages/systems/wfdl-ast';
import {TOption} from '@packages/systems/plugins/Option';
import {TDynamoCurrentItemRef} from '@packages/systems/plugins-utils/DynamoGateway';
import {
  toOperandExpr,
  TOperand,
} from '@packages/systems/plugins/ConditionsOperand';
import {
  BooleanTag,
  FALSE,
  TPredicate,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import type {DynamoEnvValueType} from '../types';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {
  RefConditionValue,
  toListOfIds,
  TRefAnyOfConditionValue,
} from '../dataTypes/RefAnyOfConditionValue';

export const CurrentItemRefType = TypeImplementation.Custom({
  params: ['referencedCollectionId'],
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('ItemRef'))),
});

export const dynamoCurrentItemRefCondition: EnvTypes.TypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        TOption(TDynamoCurrentItemRef(a)),
        TOperand(TOption(TDynamoCurrentItemRef(a)))
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: toOperandExpr,
};

export const dynamoCurrentItemRefIsAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoCurrentItemRef(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value: anyOfPredicate(TRUE, FALSE),
};

export const dynamoCurrentItemRefIsNotAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoCurrentItemRef(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value: anyOfPredicate(FALSE, TRUE),
};

/**
 * A generic any of predicate function builder for current item conditions.
 *
 * @param matchValue - BooleanTag returned if item matches at least one ref
 * @param noMatchValue - BooleanTag returned if item does not match any refs
 */
function anyOfPredicate(matchValue: BooleanTag, noMatchValue: BooleanTag) {
  return (refs: RefConditionValue[]) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      if (x === null || x.tag === 'None') {
        return noMatchValue;
      }

      const itemRefVal = x.val;

      if (
        itemRefVal == null ||
        typeof itemRefVal !== 'object' ||
        !('id' in itemRefVal) ||
        typeof itemRefVal.id !== 'string'
      ) {
        return noMatchValue;
      }

      if (!toListOfIds(refs).includes(itemRefVal.id)) {
        return noMatchValue;
      }

      return matchValue;
    };
}
